import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { getErrorMeta } from '../errors';
import { AgentCrashReportView } from './AgentCrashReportView';
import { ChatSettingsPanel } from './ChatSettingsPanel';
import { MessageBubble } from './MessageBubble';
import { ModeSelector } from './ModeSelector';
import { PlanModal } from './PlanModal';
import { PlanView } from './PlanView';
import { RawFallbackView } from './RawFallbackView';
import { SlashCommandPalette } from './SlashCommandPalette';
import { StickyPlanButton } from './StickyPlanButton';
import { ThinkingBlock } from './ThinkingBlock';
import { ToolCallCard } from './ToolCallCard';
import { UsageIndicator } from './UsageIndicator';
import { VoiceButton } from './VoiceButton';
// =============================================================================
// Client-side commands (not forwarded to agent)
// =============================================================================
export const CLIENT_COMMANDS = [
    { name: 'clear', description: 'Clear chat history', source: 'client' },
    { name: 'copy', description: 'Copy last response to clipboard', source: 'client' },
    { name: 'export', description: 'Export conversation as markdown', source: 'client' },
];
/**
 * Main conversation container for structured agent interaction.
 * Renders message list, prompt input, slash command palette, voice button, and usage indicator.
 */
export const AgentPanel = React.forwardRef(function AgentPanel({ session, messages, availableCommands = [], modes, currentMode, onSelectMode, transcribeApiUrl, ttsApiUrl, agentSettings, agentSettingsLoading, permissionModes, onSaveSettings, onError, }, ref) {
    const [input, setInput] = useState('');
    const [showPalette, setShowPalette] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const virtuosoRef = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const inputRef = useRef(null);
    const paletteRef = useRef(null);
    // Derive current plan from conversation items (only one plan exists at a time)
    const currentPlan = useMemo(() => messages.items.find((i) => i.kind === 'plan'), [messages.items]);
    // After replay completes (replaying → ready/prompting), scroll to bottom.
    const prevSessionStateRef = useRef(session.state);
    useEffect(() => {
        const prev = prevSessionStateRef.current;
        prevSessionStateRef.current = session.state;
        if (prev === 'replaying' && (session.state === 'ready' || session.state === 'prompting')) {
            virtuosoRef.current?.scrollToIndex({ index: 'LAST', behavior: 'auto' });
        }
    }, [session.state]);
    useImperativeHandle(ref, () => ({
        focusInput: () => inputRef.current?.focus(),
    }));
    // Merge agent commands with client commands for the palette
    const allCommands = useMemo(() => [...availableCommands, ...CLIENT_COMMANDS], [availableCommands]);
    // Derive the filter text from the input
    const slashFilter = useMemo(() => {
        const trimmed = input.trimStart();
        if (!trimmed.startsWith('/'))
            return null;
        // Only show palette when the input is a single word starting with /
        // (i.e., no spaces after the command — once they add args, palette hides)
        const afterSlash = trimmed.slice(1);
        if (afterSlash.includes(' '))
            return null;
        return afterSlash;
    }, [input]);
    // Show/hide palette based on slash filter
    useEffect(() => {
        setShowPalette(slashFilter !== null);
    }, [slashFilter]);
    // Handle client-side command execution
    const handleClientCommand = useCallback((cmd, _fullText) => {
        switch (cmd.name) {
            case 'clear':
                messages.clear();
                break;
            case 'copy': {
                // Find last agent message and copy it
                const lastAgent = [...messages.items]
                    .reverse()
                    .find((item) => item.kind === 'agent_message');
                if (lastAgent && lastAgent.kind === 'agent_message') {
                    void navigator.clipboard.writeText(lastAgent.text);
                }
                break;
            }
            case 'export': {
                const markdown = exportConversationAsMarkdown(messages.items);
                downloadTextFile(markdown, 'conversation.md');
                break;
            }
        }
    }, [messages]);
    // Handle slash command selection from the palette
    const handleCommandSelect = useCallback((cmd) => {
        setShowPalette(false);
        // For client commands, execute immediately
        if (cmd.source === 'client') {
            handleClientCommand(cmd, `/${cmd.name}`);
            setInput('');
            return;
        }
        // For agent commands, replace input with the command and submit
        const commandText = `/${cmd.name}`;
        setInput('');
        messages.addUserMessage(commandText);
        session.sendMessage({
            jsonrpc: '2.0',
            method: 'session/prompt',
            id: Date.now(),
            params: {
                prompt: [{ type: 'text', text: commandText }],
            },
        });
    }, [handleClientCommand, messages, session]);
    const handleSubmit = (e) => {
        e.preventDefault();
        const text = input.trim();
        if (!text || session.state !== 'ready')
            return;
        // Check for client-side commands
        if (text.startsWith('/')) {
            const cmdName = (text.slice(1).split(' ')[0] ?? '').toLowerCase();
            const clientCmd = CLIENT_COMMANDS.find((c) => c.name === cmdName);
            if (clientCmd) {
                handleClientCommand(clientCmd, text);
                setInput('');
                return;
            }
        }
        // Send to agent as normal
        messages.addUserMessage(text);
        session.sendMessage({
            jsonrpc: '2.0',
            method: 'session/prompt',
            id: Date.now(),
            params: {
                prompt: [{ type: 'text', text }],
            },
        });
        setInput('');
    };
    const handleKeyDown = (e) => {
        // When palette is visible, delegate navigation keys to it
        if (showPalette && paletteRef.current) {
            const consumed = paletteRef.current.handleKeyDown(e);
            if (consumed)
                return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };
    // Handle voice transcription — append transcribed text to current input
    const handleTranscription = useCallback((text) => {
        setInput((prev) => {
            const separator = prev.length > 0 && !prev.endsWith(' ') ? ' ' : '';
            return prev + separator + text;
        });
        // Focus the input after transcription so user can review/edit
        inputRef.current?.focus();
    }, []);
    const isPrompting = session.state === 'prompting';
    const isReconnecting = session.state === 'reconnecting';
    const isError = session.state === 'error';
    const canSend = session.state === 'ready' && input.trim().length > 0;
    // Determine placeholder text based on connection state
    const placeholderText = useMemo(() => {
        if (session.state === 'ready')
            return 'Send a message... (type / for commands)';
        if (isReconnecting)
            return 'Reconnecting...';
        if (isError && session.errorCode) {
            const meta = getErrorMeta(session.errorCode);
            return meta.userMessage;
        }
        if (isError)
            return 'Connection lost';
        return 'Waiting for agent...';
    }, [session.state, session.errorCode, isReconnecting, isError]);
    return (_jsxs("div", { className: "flex flex-col h-full bg-gray-50 rounded-lg overflow-hidden", children: [isReconnecting && (_jsx("div", { className: "bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-sm text-yellow-700 text-center", children: "Reconnecting to agent..." })), isError && _jsx(ErrorBanner, { session: session }), modes && modes.length > 0 && onSelectMode && (_jsx("div", { className: "border-b border-gray-200 bg-white px-4 py-2", children: _jsx(ModeSelector, { modes: modes, currentMode: currentMode ?? null, onSelectMode: onSelectMode }) })), _jsxs("div", { className: "relative flex-1 min-h-0", role: "log", "aria-live": "polite", "aria-label": "Conversation", children: [messages.items.length === 0 && !isReconnecting ? (_jsx("div", { className: "flex items-center justify-center h-full text-gray-400 text-sm", children: "Send a message to start the conversation" })) : (_jsx(Virtuoso, { ref: virtuosoRef, style: { height: '100%' }, data: messages.items, initialTopMostItemIndex: messages.items.length - 1, followOutput: (atBottom) => atBottom ? 'smooth' : false, alignToBottom: true, atBottomThreshold: 50, atBottomStateChange: setIsAtBottom, overscan: 200, itemContent: (_index, item) => (_jsx("div", { className: "px-4 py-0.5", children: _jsx(ConversationItemView, { item: item, ttsApiUrl: ttsApiUrl }) })) })), !isAtBottom && messages.items.length > 0 && (_jsx("button", { type: "button", onClick: () => virtuosoRef.current?.scrollToIndex({ index: 'LAST', behavior: 'smooth' }), className: "absolute bottom-3 right-5 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-300 shadow-md text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors", "aria-label": "Scroll to bottom", title: "Scroll to bottom", children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("polyline", { points: "6 9 12 15 18 9" }) }) }))] }), _jsxs("div", { className: "border-t border-gray-200 bg-white p-3 relative", children: [showSettings && onSaveSettings && permissionModes && (_jsx(ChatSettingsPanel, { settings: agentSettings ?? null, loading: agentSettingsLoading, permissionModes: permissionModes, onSave: onSaveSettings, onClose: () => setShowSettings(false) })), _jsxs("div", { className: "flex items-center gap-2 mb-2", children: [onSaveSettings && (_jsxs("button", { type: "button", onClick: () => setShowSettings((prev) => !prev), className: `flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${showSettings
                                    ? 'bg-blue-50 border-blue-300 text-blue-600'
                                    : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'}`, "aria-label": "Agent settings", title: "Agent settings", children: [_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "flex-shrink-0", children: [_jsx("circle", { cx: "12", cy: "12", r: "3" }), _jsx("path", { d: "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" })] }), _jsx("span", { children: "Settings" })] })), _jsx(StickyPlanButton, { plan: currentPlan, onClick: () => setShowPlanModal(true) }), currentPlan && (_jsx(PlanModal, { plan: currentPlan, isOpen: showPlanModal, onClose: () => setShowPlanModal(false) })), _jsxs("button", { type: "button", onClick: () => session.sendMessage({ jsonrpc: '2.0', method: 'session/cancel', params: {} }), className: `ml-auto flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${isPrompting
                                    ? 'border-red-300 bg-red-50 text-red-600 hover:bg-red-100'
                                    : 'border-gray-300 bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`, "aria-label": "Cancel agent", title: "Send cancel signal to agent", children: [_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "flex-shrink-0", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("line", { x1: "15", y1: "9", x2: "9", y2: "15" }), _jsx("line", { x1: "9", y1: "9", x2: "15", y2: "15" })] }), _jsx("span", { children: "Cancel" })] })] }), _jsx(SlashCommandPalette, { ref: paletteRef, commands: allCommands, filter: slashFilter ?? '', onSelect: handleCommandSelect, onDismiss: () => setShowPalette(false), visible: showPalette }), _jsxs("form", { onSubmit: handleSubmit, className: "flex items-end space-x-2", children: [_jsx("textarea", { ref: inputRef, value: input, onChange: (e) => setInput(e.target.value), onKeyDown: handleKeyDown, placeholder: placeholderText, disabled: session.state !== 'ready', rows: 1, className: "flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500" }), transcribeApiUrl && (_jsx(VoiceButton, { onTranscription: handleTranscription, disabled: session.state !== 'ready', apiUrl: transcribeApiUrl, onError: onError })), _jsx("button", { type: "submit", disabled: !canSend, className: "px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed", style: { minHeight: 44 }, children: "Send" })] }), _jsx("div", { className: "mt-2 flex justify-end", children: _jsx(UsageIndicator, { usage: messages.usage }) })] })] }));
});
/** Error banner with structured error code, message, and suggested action */
function ErrorBanner({ session }) {
    const meta = session.errorCode ? getErrorMeta(session.errorCode) : null;
    const userMessage = meta?.userMessage ?? session.error ?? 'Connection lost';
    const suggestedAction = meta?.suggestedAction;
    const severity = meta?.severity ?? 'recoverable';
    // Show the raw error if it provides more detail than the generic userMessage
    const detailedError = session.error && session.error !== userMessage && session.error !== meta?.userMessage
        ? session.error
        : null;
    // Color scheme based on severity
    const colors = severity === 'fatal'
        ? { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', hint: 'text-red-600' }
        : severity === 'transient'
            ? { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', hint: 'text-yellow-600' }
            : { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', hint: 'text-red-600' };
    const showReconnect = severity !== 'fatal' && severity !== 'transient' && session.errorCode !== 'NETWORK_OFFLINE';
    return (_jsxs("div", { className: `${colors.bg} border-b ${colors.border} px-4 py-2 text-sm text-center`, children: [_jsxs("div", { className: `flex items-center justify-center gap-2 ${colors.text}`, children: [_jsx("span", { className: "font-medium", children: userMessage }), showReconnect && (_jsx("button", { type: "button", onClick: () => session.reconnect(), className: "px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700", style: { minHeight: 32 }, children: "Reconnect" }))] }), detailedError && (_jsx("p", { className: `text-xs mt-0.5 ${colors.hint} truncate max-w-lg mx-auto`, title: detailedError, children: detailedError })), suggestedAction && (_jsx("p", { className: `text-xs mt-1 ${colors.hint}`, children: suggestedAction }))] }));
}
/**
 * Routes a ConversationItem to the appropriate component.
 *
 * Wrapped in React.memo so that parent re-renders (input changes, scroll
 * state toggles, palette visibility) don't cascade into every conversation
 * item. Only re-renders when the item object itself changes.
 */
const ConversationItemView = React.memo(function ConversationItemView({ item, ttsApiUrl }) {
    switch (item.kind) {
        case 'user_message':
            return _jsx(MessageBubble, { text: item.text, role: "user" });
        case 'agent_message':
            return _jsx(MessageBubble, { text: item.text, role: "agent", streaming: item.streaming, timestamp: item.timestamp, ttsApiUrl: ttsApiUrl, ttsStorageId: item.id });
        case 'thinking':
            return _jsx(ThinkingBlock, { text: item.text, active: item.active });
        case 'tool_call':
            return _jsx(ToolCallCard, { toolCall: item });
        case 'plan':
            return _jsx(PlanView, { plan: item });
        case 'agent_crash_report':
            return _jsx(AgentCrashReportView, { item: item });
        case 'raw_fallback':
            return _jsx(RawFallbackView, { item: item });
        default:
            return null;
    }
});
// =============================================================================
// Helpers for client commands
// =============================================================================
function exportConversationAsMarkdown(items) {
    const lines = ['# Conversation Export', '', `Exported: ${new Date().toISOString()}`, ''];
    for (const item of items) {
        switch (item.kind) {
            case 'user_message':
                lines.push(`## User`, '', item.text, '');
                break;
            case 'agent_message':
                lines.push(`## Agent`, '', item.text, '');
                break;
            case 'thinking':
                lines.push(`> **Thinking:** ${item.text}`, '');
                break;
            case 'tool_call':
                lines.push(`### Tool: ${item.title}`, '');
                for (const c of item.content) {
                    if (c.text) {
                        lines.push('```', c.text, '```', '');
                    }
                }
                break;
        }
    }
    return lines.join('\n');
}
function downloadTextFile(content, filename) {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}
//# sourceMappingURL=AgentPanel.js.map